<template>
  <v-dialog v-model="internalValue" max-width="640" persistent scrollable>
    <v-card class="ds-dialog">
      <v-card-title class="ds-dialog-header d-flex align-center">
        <v-icon color="white" class="mr-2 header-icon">mdi-file-image</v-icon>
        <span class="ds-dialog-title">Timbre da Prescrição</span>
        <v-spacer />
        <v-btn icon color="white" aria-label="Ver tour" @click="startTour">
          <v-icon>mdi-help-circle-outline</v-icon>
        </v-btn>
        <v-btn icon class="close-btn" color="white" @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="ds-dialog-body">
        <!-- Seletor de escopo (médico/clínica) -->
        <section v-if="ownerOptions.length > 1" class="ds-form-section">
          <div class="ds-form-section__head">
            <div class="ds-form-section__title">
              <v-icon small color="primary" class="mr-2">mdi-account-cog</v-icon>
              <span>Salvar timbre em</span>
            </div>
          </div>
          <v-select
            v-model="selectedOwnerKey"
            :items="ownerOptions"
            item-text="label"
            item-value="key"
            outlined
            dense
            color="primary"
            hide-details
            @change="onOwnerChange"
          />
        </section>

        <section class="ds-form-section">
          <div class="ds-form-section__head">
            <div class="ds-form-section__title">
              <v-icon small color="primary" class="mr-2">mdi-file-image</v-icon>
              <span>Timbre</span>
            </div>
          </div>

          <!-- Loading inicial -->
          <div v-if="initialLoading" class="text-center py-6">
            <v-progress-circular indeterminate color="primary" />
            <div class="text-caption grey--text mt-3">Carregando timbre…</div>
          </div>

          <!-- Sem timbre — área de upload -->
          <div v-else-if="!letterheadImage" class="upload-area" data-tour="lh-upload">
            <v-file-input
              v-model="file"
              accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
              prepend-icon=""
              prepend-inner-icon="mdi-paperclip"
              label="Selecionar PDF, PNG ou JPG"
              outlined
              dense
              show-size
              persistent-hint
              hint="Recomendado: A4 com o cabeçalho do seu consultório (PDF até 10MB, imagem até 5MB)"
              @change="onFileSelected"
            />

            <div class="format-chips mt-2" data-tour="lh-formats">
              <v-chip x-small color="info" outlined class="mr-1">
                <v-icon left x-small>mdi-file-pdf-box</v-icon> PDF
              </v-chip>
              <v-chip x-small color="info" outlined class="mr-1">
                <v-icon left x-small>mdi-file-image</v-icon> PNG / JPG
              </v-chip>
            </div>

            <v-progress-linear
              v-if="processingPdf"
              indeterminate
              color="primary"
              class="mt-3"
            />
            <div v-if="processingPdf" class="text-caption text-center mt-1">
              Convertendo PDF…
            </div>
          </div>

          <!-- Com timbre — preview + ações -->
          <div v-else class="letterhead-preview">
            <div class="preview-header">
              <div class="preview-meta">
                <v-icon small color="primary" class="mr-1">mdi-image-check</v-icon>
                <span class="font-weight-bold">{{ letterheadFileName || "Timbre atual" }}</span>
              </div>
              <div>
                <v-btn
                  x-small
                  outlined
                  color="primary"
                  class="mr-2"
                  @click="triggerReplace"
                >
                  <v-icon left x-small>mdi-swap-horizontal</v-icon>
                  Substituir
                </v-btn>
                <v-btn
                  x-small
                  outlined
                  color="error"
                  @click="removeLetterhead"
                  :loading="removing"
                >
                  <v-icon left x-small>mdi-delete</v-icon>
                  Remover
                </v-btn>
              </div>
            </div>

            <div class="area-hint mt-3" data-tour="lh-area">
              <v-icon size="14" color="primary" class="mr-1">mdi-cursor-move</v-icon>
              Arraste e redimensione o retângulo para definir <strong>onde o texto da prescrição será escrito</strong>. O ajuste é salvo automaticamente.
              <v-btn x-small text color="primary" class="ml-1 reset-area-btn" @click="resetArea">
                <v-icon left x-small>mdi-backup-restore</v-icon>
                Restaurar padrão
              </v-btn>
            </div>

            <div class="preview-image-wrapper mt-2" data-tour="lh-preview">
              <div
                ref="canvas"
                class="lh-canvas"
                :style="{ backgroundImage: `url('${letterheadImage}')` }"
              >
                <!-- Sombreamento de fora da área -->
                <div class="area-rect" :style="rectStyle" @mousedown.prevent="startMove">
                  <span class="area-label">Texto da prescrição</span>
                  <span
                    v-for="h in handles"
                    :key="h"
                    class="area-handle"
                    :class="`handle-${h}`"
                    @mousedown.prevent.stop="startResize(h, $event)"
                  />
                </div>
              </div>
              <div v-if="savingArea" class="area-saving">
                <v-icon size="13" class="mr-1">mdi-content-save</v-icon> Salvando área…
              </div>
            </div>

            <!-- File input escondido pra "substituir" -->
            <input
              ref="hiddenFile"
              type="file"
              accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
              style="display:none"
              @change="onReplaceFile"
            />
          </div>

          <v-alert
            v-if="errorMessage"
            type="error"
            text
            dense
            class="mt-3"
          >
            {{ errorMessage }}
          </v-alert>

          <v-progress-linear
            v-if="uploading"
            indeterminate
            color="primary"
            class="mt-3"
          />
        </section>
      </v-card-text>

      <v-card-actions class="ds-dialog-actions">
        <v-btn outlined color="primary" class="ds-cancel-btn" @click="closeDialog">
          <v-icon left>mdi-close</v-icon>
          Fechar
        </v-btn>
        <v-spacer />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { useSnackbar } from "@/composables/useSnackbar";
import {
  getLetterhead,
  uploadLetterhead,
  deleteLetterhead,
  fetchLetterheadImageUrl,
  updateLetterheadArea,
  getLetterheadPreference,
  saveLetterheadPreference,
} from "@/services/letterheadService";
import { getMyClinics } from "@/services/clinicService";
import { pdfFirstPageToPng } from "@/utils/pdfToImage";
import tourDialogMixin from "@/features/tour/tourDialogMixin";

// Área padrão (em % da folha A4) — equivale ao fallback de margens da impressão
// (≈ 55mm topo / 18mm laterais / 40mm rodapé sobre A4 210×297mm).
const DEFAULT_AREA = Object.freeze({ top: 18.5, left: 8.5, width: 83, height: 68 });
const MIN_W = 10; // largura mínima em %
const MIN_H = 8;  // altura mínima em %

export default {
  name: "LetterheadUploadDialog",
  mixins: [tourDialogMixin],
  props: {
    value: { type: Boolean, default: false },
  },
  emits: ["input", "updated"],

  setup() {
    const { success, error: snackError } = useSnackbar();
    return { success, snackError };
  },

  data() {
    return {
      // Owner
      selectedOwnerKey: "user:self",
      ownerOptions: [{ key: "user:self", label: "Meu perfil", ownerType: "user", ownerId: null }],

      // Estado
      initialLoading: false,
      uploading: false,
      removing: false,
      processingPdf: false,
      errorMessage: null,

      // Arquivo selecionado
      file: null,

      // Timbre atual
      letterheadId: null,
      letterheadImage: null,         // object URL do blob da imagem (uso no <img>)
      letterheadFileName: null,
      _imageObjectUrl: null,

      // Editor de área de conteúdo (retângulo do texto)
      area: { ...DEFAULT_AREA },     // { top, left, width, height } em % (0-100)
      savingArea: false,
      handles: ["nw", "n", "ne", "e", "se", "s", "sw", "w"],
      _drag: null,                   // estado interno do arraste/resize
    };
  },

  computed: {
    internalValue: {
      get() { return this.value; },
      set(v) { this.$emit("input", v); },
    },
    currentOwner() {
      return this.ownerOptions.find((o) => o.key === this.selectedOwnerKey) || this.ownerOptions[0];
    },
    rectStyle() {
      return {
        top: `${this.area.top}%`,
        left: `${this.area.left}%`,
        width: `${this.area.width}%`,
        height: `${this.area.height}%`,
      };
    },
    tourId() {
      return "dialog-timbrado";
    },
  },

  watch: {
    value(open) {
      if (open) {
        this.initialize();
        this.tourOnOpen();
      } else {
        this.cleanup();
      }
    },
  },

  beforeDestroy() {
    this.cleanup();
  },

  methods: {
    tourReady() {
      return !this.initialLoading;
    },

    tourSteps() {
      return [
        {
          title: "Timbre da prescrição",
          description:
            "O timbre é o papel timbrado (cabeçalho) que aparece nas prescrições impressas em PDF. Vamos ver como configurá-lo.",
        },
        {
          anchor: "lh-upload",
          title: "Enviar o timbre",
          description:
            "Selecione aqui o arquivo do seu timbre. Ele será usado como fundo de todas as prescrições impressas.",
        },
        {
          anchor: "lh-formats",
          title: "Formatos aceitos",
          description:
            "Você pode enviar um PDF (recomendado, tamanho A4) ou uma imagem PNG/JPG com o cabeçalho do seu consultório.",
        },
        {
          anchor: "lh-area",
          title: "Área do texto",
          description:
            "Com o timbre carregado, arraste e redimensione o retângulo para definir onde o texto da prescrição será escrito. O ajuste é salvo automaticamente.",
        },
        {
          anchor: "lh-preview",
          title: "Prévia do timbre",
          description:
            "Esta é a prévia da folha. Ela mostra exatamente como o timbre e o texto ficarão na prescrição impressa.",
        },
      ];
    },

    async initialize() {
      this.errorMessage = null;
      this.initialLoading = true;
      try {
        await this.loadOwnerOptions();
        await this.applySavedOwnerPreference();
        await this.loadCurrentLetterhead();
      } catch (err) {
        console.error(err);
        this.errorMessage = "Não foi possível carregar o timbre.";
      } finally {
        this.initialLoading = false;
      }
    },

    async loadOwnerOptions() {
      // Sempre tem a opção "Meu perfil"
      const opts = [{ key: "user:self", label: "Meu perfil (médico)", ownerType: "user", ownerId: null }];

      // Adiciona as clínicas em que o usuário participa
      try {
        const clinics = await getMyClinics();
        (clinics || []).forEach((c) => {
          opts.push({
            key: `clinic:${c.id}`,
            label: `Clínica: ${c.name}`,
            ownerType: "clinic",
            ownerId: c.id,
          });
        });
      } catch (e) {
        // Não bloqueia — só não terá opção de clínica
        console.warn("Falha ao listar clínicas pro seletor de timbre:", e);
      }

      this.ownerOptions = opts;
      if (!opts.find((o) => o.key === this.selectedOwnerKey)) {
        this.selectedOwnerKey = "user:self";
      }
    },

    async loadCurrentLetterhead() {
      this.revokeImageUrl();
      this.letterheadId = null;
      this.letterheadImage = null;
      this.letterheadFileName = null;

      const params = {};
      if (this.currentOwner.ownerType === "clinic") {
        params.owner_type = "clinic";
        params.owner_id = this.currentOwner.ownerId;
      }

      const letterhead = await getLetterhead(params);
      if (!letterhead) return;

      this.letterheadId = letterhead.id;
      this.letterheadFileName = letterhead.file_name;
      this.area = this.sanitizeArea(letterhead.content_area);

      try {
        const url = await fetchLetterheadImageUrl(letterhead.id);
        this._imageObjectUrl = url;
        this.letterheadImage = url;
      } catch (err) {
        console.error("Erro ao baixar imagem do timbre:", err);
      }
    },

    /**
     * Reabre o diálogo já no escopo que o usuário escolheu da última vez —
     * é essa mesma escolha que as prescrições usam ao imprimir/baixar.
     */
    async applySavedOwnerPreference() {
      const preference = await getLetterheadPreference();
      if (!preference) return;

      const key =
        preference.owner_type === "clinic"
          ? `clinic:${preference.owner_id}`
          : "user:self";

      // Se a clínica saiu da lista (usuário deixou a clínica), mantém o padrão.
      if (this.ownerOptions.some((o) => o.key === key)) {
        this.selectedOwnerKey = key;
      }
    },

    async onOwnerChange() {
      this.initialLoading = true;
      try {
        await this.loadCurrentLetterhead();
        // A escolha do escopo é o "timbre ativo" das prescrições, então
        // precisa sobreviver ao fechamento do diálogo.
        await saveLetterheadPreference({
          ownerType: this.currentOwner.ownerType,
          ownerId: this.currentOwner.ownerId,
        }).catch((e) =>
          console.warn("Não foi possível salvar a preferência de timbre:", e),
        );
      } finally {
        this.initialLoading = false;
      }
    },

    onFileSelected(fileOrFiles) {
      const file = Array.isArray(fileOrFiles) ? fileOrFiles[0] : fileOrFiles;
      if (!file) return;
      this.processAndUpload(file);
    },

    triggerReplace() {
      this.$refs.hiddenFile?.click();
    },

    onReplaceFile(e) {
      const file = e.target.files?.[0];
      if (!file) return;
      this.processAndUpload(file);
      // limpa pra permitir reescolher o mesmo arquivo
      e.target.value = "";
    },

    async processAndUpload(file) {
      this.errorMessage = null;

      const isPdf = /pdf$/i.test(file.type) || /\.pdf$/i.test(file.name);
      const isImage = /^image\/(png|jpe?g)$/i.test(file.type) || /\.(png|jpe?g)$/i.test(file.name);

      if (!isPdf && !isImage) {
        this.errorMessage = "Formato não suportado. Use PDF, PNG ou JPG.";
        this.file = null;
        return;
      }

      let imageBlob;
      let pdfBlob = null;

      try {
        if (isPdf) {
          this.processingPdf = true;
          imageBlob = await pdfFirstPageToPng(file, { scale: 2 });
          pdfBlob = file;
        } else {
          imageBlob = file;
        }
      } catch (err) {
        console.error("Erro ao converter PDF:", err);
        this.errorMessage = "Não foi possível ler o PDF. Tente outro arquivo.";
        this.processingPdf = false;
        this.file = null;
        return;
      } finally {
        this.processingPdf = false;
      }

      // Upload
      this.uploading = true;
      try {
        const payload = {
          imageBlob,
          pdfBlob,
          fileName: file.name,
        };
        if (this.currentOwner.ownerType === "clinic") {
          payload.ownerType = "clinic";
          payload.ownerId = this.currentOwner.ownerId;
        }
        const letterhead = await uploadLetterhead(payload);
        this.letterheadId = letterhead.id;
        this.letterheadFileName = letterhead.file_name;
        // Substituir mantém a área salva; timbre novo começa no padrão.
        this.area = this.sanitizeArea(letterhead.content_area);

        // Renova object URL
        this.revokeImageUrl();
        const url = await fetchLetterheadImageUrl(letterhead.id);
        this._imageObjectUrl = url;
        this.letterheadImage = url;

        this.success("Timbre salvo com sucesso!");
        this.$emit("updated", letterhead);
        this.file = null;
      } catch (err) {
        console.error("Erro ao enviar timbre:", err);
        this.errorMessage =
          err?.response?.data?.error ||
          "Falha ao enviar o timbre. Tente novamente.";
      } finally {
        this.uploading = false;
      }
    },

    async removeLetterhead() {
      if (!this.letterheadId) return;
      const ok = await this.$confirm({
        type: "delete",
        title: "Remover timbre",
        message: "Tem certeza que deseja remover o timbre?",
        confirmText: "Remover",
      });
      if (!ok) return;

      this.removing = true;
      this.errorMessage = null;
      try {
        await deleteLetterhead(this.letterheadId);
        this.revokeImageUrl();
        this.letterheadId = null;
        this.letterheadImage = null;
        this.letterheadFileName = null;
        this.success("Timbre removido.");
        this.$emit("updated", null);
      } catch (err) {
        console.error("Erro ao remover timbre:", err);
        this.errorMessage = "Não foi possível remover o timbre.";
      } finally {
        this.removing = false;
      }
    },

    // ── Editor de área de conteúdo ──────────────────────────────────────
    sanitizeArea(raw) {
      if (!raw || typeof raw !== "object") return { ...DEFAULT_AREA };
      const num = (v, d) => (Number.isFinite(+v) ? +v : d);
      const left = this.clamp(num(raw.left, DEFAULT_AREA.left), 0, 100 - MIN_W);
      const top = this.clamp(num(raw.top, DEFAULT_AREA.top), 0, 100 - MIN_H);
      const width = this.clamp(num(raw.width, DEFAULT_AREA.width), MIN_W, 100 - left);
      const height = this.clamp(num(raw.height, DEFAULT_AREA.height), MIN_H, 100 - top);
      return { top, left, width, height };
    },

    clamp(v, min, max) {
      return Math.max(min, Math.min(max, v));
    },

    startMove(e) {
      this.beginDrag("move", null, e);
    },

    startResize(handle, e) {
      this.beginDrag("resize", handle, e);
    },

    beginDrag(mode, handle, e) {
      const rect = this.$refs.canvas?.getBoundingClientRect();
      if (!rect) return;
      const a = this.area;
      this._drag = {
        mode,
        handle,
        rect,
        startX: e.clientX,
        startY: e.clientY,
        edges: { top: a.top, left: a.left, right: a.left + a.width, bottom: a.top + a.height },
      };
      window.addEventListener("mousemove", this.onDragMove);
      window.addEventListener("mouseup", this.onDragEnd);
    },

    onDragMove(e) {
      const d = this._drag;
      if (!d) return;
      const dxPct = ((e.clientX - d.startX) / d.rect.width) * 100;
      const dyPct = ((e.clientY - d.startY) / d.rect.height) * 100;
      const s = d.edges;

      if (d.mode === "move") {
        const w = s.right - s.left;
        const h = s.bottom - s.top;
        const left = this.clamp(s.left + dxPct, 0, 100 - w);
        const top = this.clamp(s.top + dyPct, 0, 100 - h);
        this.area = { top, left, width: w, height: h };
        return;
      }

      // resize — move só as bordas afetadas pela alça
      let { top, left, right, bottom } = s;
      const hnd = d.handle;
      if (hnd.includes("n")) top = this.clamp(s.top + dyPct, 0, s.bottom - MIN_H);
      if (hnd.includes("s")) bottom = this.clamp(s.bottom + dyPct, s.top + MIN_H, 100);
      if (hnd.includes("w")) left = this.clamp(s.left + dxPct, 0, s.right - MIN_W);
      if (hnd.includes("e")) right = this.clamp(s.right + dxPct, s.left + MIN_W, 100);

      this.area = { top, left, width: right - left, height: bottom - top };
    },

    onDragEnd() {
      window.removeEventListener("mousemove", this.onDragMove);
      window.removeEventListener("mouseup", this.onDragEnd);
      this._drag = null;
      this.saveArea();
    },

    async saveArea() {
      if (!this.letterheadId) return;
      this.savingArea = true;
      try {
        const payload = {
          top: Math.round(this.area.top * 100) / 100,
          left: Math.round(this.area.left * 100) / 100,
          width: Math.round(this.area.width * 100) / 100,
          height: Math.round(this.area.height * 100) / 100,
        };
        const letterhead = await updateLetterheadArea(this.letterheadId, payload);
        this.$emit("updated", letterhead);
      } catch (err) {
        console.error("Erro ao salvar área do timbre:", err);
        this.snackError?.("Não foi possível salvar a área do texto.");
      } finally {
        this.savingArea = false;
      }
    },

    resetArea() {
      this.area = { ...DEFAULT_AREA };
      this.saveArea();
    },

    revokeImageUrl() {
      if (this._imageObjectUrl) {
        URL.revokeObjectURL(this._imageObjectUrl);
        this._imageObjectUrl = null;
      }
    },

    cleanup() {
      // remove listeners caso o dialog feche no meio de um arraste
      window.removeEventListener("mousemove", this.onDragMove);
      window.removeEventListener("mouseup", this.onDragEnd);
      this._drag = null;
      this.savingArea = false;
      this.area = { ...DEFAULT_AREA };
      this.revokeImageUrl();
      this.letterheadImage = null;
      this.letterheadId = null;
      this.letterheadFileName = null;
      this.file = null;
      this.errorMessage = null;
    },

    closeDialog() {
      this.$emit("input", false);
    },
  },
};
</script>

<style scoped>
.ds-dialog {
  border-radius: 4px !important;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(31, 77, 63, 0.18),
    0 4px 12px rgba(0, 0, 0, 0.06) !important;
}
.header-icon {
  position: relative;
  z-index: 1;
}
.close-btn {
  position: relative;
  z-index: 1;
}
.close-btn:hover {
  background-color: rgba(255, 255, 255, 0.15) !important;
}

.upload-area {
  background: var(--ds-blue-50);
  border: 1px dashed var(--ds-blue-200);
  border-radius: 12px;
  padding: 18px;
}

.format-chips {
  display: flex;
  justify-content: center;
}

.preview-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
}
.preview-meta {
  display: flex;
  align-items: center;
  font-size: 13px;
  color: var(--ds-text-strong);
}
.area-hint {
  font-size: 11.5px;
  line-height: 1.5;
  color: var(--ds-text-600);
  background: var(--ds-surface-white);
  border: 1px solid var(--ds-blue-100);
  border-radius: 8px;
  padding: 8px 12px;
}
.reset-area-btn {
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-size: 11px !important;
}

.preview-image-wrapper {
  background: var(--ds-surface-white);
  border: 1px solid var(--ds-blue-100);
  border-radius: 8px;
  padding: 12px;
}

/* Folha A4 — mesma proporção e bg-size da impressão, pra o retângulo bater
   exatamente com o que sai impresso. */
.lh-canvas {
  position: relative;
  width: 100%;
  max-width: 420px;
  margin: 0 auto;
  aspect-ratio: 210 / 297;
  background-size: 100% 100%;
  background-position: top left;
  background-repeat: no-repeat;
  border: 1px solid var(--ds-blgrey-100);
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(31, 77, 63, 0.12);
  user-select: none;
  touch-action: none;
}

.area-rect {
  position: absolute;
  border: 2px dashed var(--ds-primary);
  background: rgba(31, 77, 63, 0.1);
  cursor: move;
  box-sizing: border-box;
}
.area-label {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 10px;
  font-weight: 700;
  color: var(--ds-primary-dark);
  background: rgba(255, 255, 255, 0.85);
  padding: 2px 6px;
  border-radius: 4px;
  white-space: nowrap;
  pointer-events: none;
}

.area-handle {
  position: absolute;
  width: 12px;
  height: 12px;
  background: var(--ds-surface-white);
  border: 2px solid var(--ds-primary);
  border-radius: 50%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
}
.handle-nw { top: -6px; left: -6px; cursor: nwse-resize; }
.handle-n  { top: -6px; left: 50%; transform: translateX(-50%); cursor: ns-resize; }
.handle-ne { top: -6px; right: -6px; cursor: nesw-resize; }
.handle-e  { top: 50%; right: -6px; transform: translateY(-50%); cursor: ew-resize; }
.handle-se { bottom: -6px; right: -6px; cursor: nwse-resize; }
.handle-s  { bottom: -6px; left: 50%; transform: translateX(-50%); cursor: ns-resize; }
.handle-sw { bottom: -6px; left: -6px; cursor: nesw-resize; }
.handle-w  { top: 50%; left: -6px; transform: translateY(-50%); cursor: ew-resize; }

.area-saving {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  color: var(--ds-primary);
  margin-top: 8px;
}

.ds-cancel-btn {
  font-size: 13px !important;
  font-weight: 600 !important;
  letter-spacing: 0.2px !important;
  text-transform: none !important;
  height: 38px !important;
}
</style>
